import React from "react";
import { Box, Grid2, Typography, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";

import ResponsiveImage from "./ResponsiveImage";
// Single source of truth for the site-wide footer. Drop <Footer /> at the
// end of any page that needs it; the AppBar's "Contact us" / "About us"
// nav entries scroll to id="footer", so keep that anchor here.
const INSURANCE_LINKS = [
  { label: "Motor Insurance", path: "/motor-landing" },
  { label: "Travel Insurance", path: "/travel-landing" },
  { label: "Pet Insurance", path: "/pet-landing" },
  { label: "Life Insurance", path: "/coming-soon?product=life" },
];

const Footer = () => {
  const navigate = useNavigate();

  return (
    <Box
      id="footer"
      sx={{
        background: "linear-gradient(180deg, #FBF1E2 0%, #f5e9d2 100%)",
        borderTop: "1px solid rgba(123,110,102,0.10)",
      }}
    >
      <Grid2 container spacing={4} sx={{ py: 6, px: { xs: 4, md: 10 } }}>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
            <ResponsiveImage
              src="/assets/images/logo-with-name.png"
              alt="SmartSure"
              sizes="298px"
              sx={{ height: 100, width: "auto" }}
            />
          </Box>
          <Typography
            sx={{
              color: "#7B6E66",
              fontSize: "0.9375rem",
              lineHeight: 1.7,
              maxWidth: 320,
            }}
          ></Typography>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 2 }}>
          <Typography
            variant="overline"
            sx={{
              color: "#a1885e",
              fontWeight: 700,
              letterSpacing: 1.6,
              fontSize: "0.72rem",
            }}
          >
            Insurance
          </Typography>
          {INSURANCE_LINKS.map((link) => (
            <Typography
              key={link.path}
              variant="body1"
              sx={{
                mt: 1.25,
                cursor: "pointer",
                color: "#3d322b",
                fontWeight: 500,
                fontSize: "0.9375rem",
                transition: "color .2s ease",
                ":hover": { color: "#C9A777" },
              }}
              onClick={() => navigate(link.path)}
            >
              {link.label}
            </Typography>
          ))}
        </Grid2>

        <Grid2 size={{ xs: 12, md: 3 }}>
          <Typography
            variant="overline"
            sx={{
              color: "#a1885e",
              fontWeight: 700,
              letterSpacing: 1.6,
              fontSize: "0.72rem",
            }}
          >
            About Us
          </Typography>
          <Typography
            sx={{
              color: "#3d322b",
              mt: 1.25,
              fontSize: "0.9375rem",
              lineHeight: 1.7,
            }}
          >
            Welcome to <b>SmartSure</b> — your trusted partner in digital
            innovation and strategic growth.
            <br />
            At <b>SmartSure</b>, we specialize in delivering cutting-edge
            solutions that empower businesses to thrive in today's fast-paced
            digital world. Whether you're a startup looking to build your
            presence or an established organization seeking to enhance
            performance, our dedicated team is here to support you every step
            of the way.
          </Typography>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 3 }}>
          <Typography
            variant="overline"
            sx={{
              color: "#a1885e",
              fontWeight: 700,
              letterSpacing: 1.6,
              fontSize: "0.72rem",
            }}
          >
            Contact Us
          </Typography>
          <Stack spacing={1.25} sx={{ mt: 1.25 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <WhatsAppIcon sx={{ color: "#25D366", fontSize: 18 }} />
              <Box>
                <a
                  href="https://wa.me/971509454560"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    textDecoration: "none",
                    color: "#3d322b",
                    fontWeight: 500,
                  }}
                >
                  +971 50 945 4560
                </a>
              </Box>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PhoneOutlinedIcon sx={{ color: "#a1885e", fontSize: 18 }} />
              <a
                href="https://wa.me/971509198749"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: "none",
                  color: "#3d322b",
                  fontWeight: 500,
                }}
              >
                +971 50 919 8749
              </a>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <EmailOutlinedIcon sx={{ color: "#a1885e", fontSize: 18 }} />
              <a
                href="mailto:hello@smartsure.ae"
                style={{
                  textDecoration: "none",
                  color: "#3d322b",
                  fontWeight: 500,
                }}
              >
                hello@smartsure.ae
              </a>
            </Stack>
          </Stack>
        </Grid2>
      </Grid2>

      <Box sx={{ px: { xs: 4, md: 12 }, pb: 4 }}>
        <Box sx={{ borderTop: "1px solid rgba(123,110,102,0.18)", pt: 3 }}>
          <Typography
            variant="body2"
            sx={{
              color: "#7B6E66",
              textAlign: "center",
              fontSize: "0.8125rem",
              lineHeight: 1.75,
              letterSpacing: 0.1,
            }}
          >
            <b>SmartSure FZ LLC</b>, licensed under the Ras Al Khaimah Economic
            Zone (RAKEZ) with license number <b>47018635</b>, operates&nbsp;
            <a
              href="https://www.smartsure.online"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                textDecoration: "none",
                color: "#a1885e",
                fontWeight: 600,
              }}
            >
              www.smartsure.online
            </a>
            , a comprehensive insurance price comparison platform.
            <br />
            Our platform enables users to compare a wide range of insurance
            products with ease and transparency.
            <br />
            <br />
            SmartSure FZ LLC is officially registered with the{" "}
            <b>Central Bank of the UAE</b> as an insurance policy price
            comparison website under registration number <b>22</b>.
            <br />
            <br />
            Should you decide to proceed with the purchase of a selected
            insurance product, you will be redirected to{" "}
            <b>Metropolitan Insurance Brokers LLC</b>—a licensed and regulated
            entity under the <b>Central Bank of the UAE</b>, holding
            registration number <b>186</b>—to securely complete your
            transaction.
            <br />
            <br />
            We highly value your feedback and are committed to enhancing your
            experience. For suggestions, inquiries, or complaints, please
            contact us at&nbsp;
            <a
              href="mailto:hello@smartsure.ae"
              style={{ textDecoration: "none", color: "inherit" }}
            >
              hello@smartsure.ae
            </a>
            &nbsp;or reach out via <br />call or WhatsApp at <b>+971 50 945 4560</b>{" "}
            or <b>+971 50 919 8749</b>.
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: "#9c8e85",
            textAlign: "center",
            pt: 3,
            fontSize: "0.75rem",
            letterSpacing: 0.4,
          }}
        >
          © 2025 SmartSure FZ LLC. All Rights Reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default Footer;
